"use client";

import { useEffect, useRef } from "react";
import { useParams, usePathname } from "next/navigation";
import useUserInfo from "@/hooks/useUserInfo";
import { userRoles } from "@/lib/common";
import contentsquare, {
  ContentsquareEvent,
  getPageViewEventFromPathname,
  isAuthPath,
  toContentsquareRole,
} from "@/lib/contentsquare";

export default function ContentsquareProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const params = useParams();
  const onAuthRoute = isAuthPath(pathname);
  const { userId, userName, email, role, companies, loading } = useUserInfo({
    enabled: !onAuthRoute,
  } as Parameters<typeof useUserInfo>[0]);

  const companyIdParam = typeof params?.companyId === "string" ? params.companyId : null;
  const lastSuperAdminCompanyRef = useRef<string | null>(null);

  useEffect(() => {
    if (onAuthRoute) return;
    if (loading || !userId || !role) return;

    const csRole = toContentsquareRole(role);
    const isSuperAdmin = role === userRoles.SUPER_ADMIN;
    const baseAttrs = {
      role: csRole,
      ...(userName ? { userName } : {}),
      ...(email ? { email } : {}),
    };

    contentsquare.init();

    if (isSuperAdmin) {
      contentsquare.identifyUser(userId, baseAttrs);
    } else {
      const activeCompany = companyIdParam
        ? companies.find(c => c._id === companyIdParam)
        : undefined;

      contentsquare.identifyUser(userId, {
        ...baseAttrs,
        ...(activeCompany
          ? {
              companyId: activeCompany._id,
              companyName: activeCompany.value,
            }
          : {}),
      });
    }

    // Fires on the first authenticated page load of a tab session, not at the login
    // action itself (auth routes are excluded from tracking).
    contentsquare.event(ContentsquareEvent.SESSION_STARTED, { dedupeKey: userId });
  }, [onAuthRoute, loading, userId, userName, email, role, companyIdParam, companies]);

  // Super Admin: keep company custom vars in sync and fire an event on company change (without a full re-identify)
  useEffect(() => {
    if (onAuthRoute) return;
    if (loading || !userId || role !== userRoles.SUPER_ADMIN) return;
    if (!companyIdParam) return;

    if (lastSuperAdminCompanyRef.current === companyIdParam) return;

    const isInitialMount = lastSuperAdminCompanyRef.current === null;
    lastSuperAdminCompanyRef.current = companyIdParam;

    const activeCompany = companies.find(c => c._id === companyIdParam);
    if (activeCompany) {
      contentsquare.setCompanyContext(activeCompany._id, activeCompany.value);
    }

    // Skip the initial-mount event so we don't double-count with the home picker
    if (isInitialMount) return;

    contentsquare.event(ContentsquareEvent.COMPANY_SELECTED, { dedupeKey: companyIdParam });
  }, [onAuthRoute, loading, userId, role, companyIdParam, companies]);

  // Page-view events for main authenticated routes (once per company/page per session)
  useEffect(() => {
    if (onAuthRoute) return;
    if (loading || !userId || !role) return;

    const pageEvent = getPageViewEventFromPathname(pathname);
    if (!pageEvent) return;

    // CSR roles are redirected away from the Overview dashboard — don't count it
    if (
      pageEvent === ContentsquareEvent.DASHBOARD_VIEWED &&
      (role === userRoles.CSR_USER || role === userRoles.SUPER_CSR_ADMIN)
    ) {
      return;
    }

    const dedupeKey =
      pageEvent === ContentsquareEvent.HOME_VIEWED ? userId : companyIdParam || userId;

    contentsquare.event(pageEvent, { dedupeKey });
  }, [onAuthRoute, loading, userId, role, pathname, companyIdParam]);

  return <>{children}</>;
}
